"use client";

import { useTranslations } from "next-intl";
import { useMemo } from "react";
import {
  computeGiftSetBundleAdjustments,
  uniqueGiftSetDisplayTitlesFromDetections,
} from "@/features/gift-sets/shared/cartPricing";
import { useGiftSets } from "@/features/gift-sets/ui";
import { computeShippingFee } from "@/features/shipping/shared/shippingFee";
import { useShippingSettings } from "@/features/shipping/ui/hooks/useShippingSettings";
import { useCountryStore, useShoppingCartStore } from "@/lib/client/stores";
import { getTotal } from "@/lib/shared/utils/calculatePrice";
import { HStack, Separator, Text, VStack } from "@/ui";
import { CartItem } from "./CartItem";

interface CartContentProps {
  shippingFee?: number;
  deliveryLabel?: string;
}

/**
 * CartContent component - Displays cart items and order summary
 */
export const CartContent = ({ shippingFee: shippingFeeProp, deliveryLabel }: CartContentProps) => {
  const shoppingCartT = useTranslations("shopping_cart");
  const giftSetsT = useTranslations("gift_sets");
  const { cartItems } = useShoppingCartStore();
  const { country } = useCountryStore();
  const giftSets = useGiftSets();
  const { settings } = useShippingSettings();

  const shippingFee = useMemo(() => {
    if (shippingFeeProp != null) return shippingFeeProp;
    return computeShippingFee(settings, "standard", country.exchangeRate);
  }, [shippingFeeProp, settings, country.exchangeRate]);

  const deliveryLineLabel = deliveryLabel ?? shoppingCartT("delivery_hint");

  const { lineSubtotal, totalSavings, adjustedSubtotal, uniqueDetectedTitles } = useMemo(() => {
    const rate = country.exchangeRate;
    const line = getTotal(cartItems, rate);
    const { totalSavings: savings, detections } = computeGiftSetBundleAdjustments(
      cartItems,
      rate,
      giftSets,
    );
    return {
      lineSubtotal: line,
      totalSavings: savings,
      adjustedSubtotal: Number((line - savings).toFixed(2)),
      uniqueDetectedTitles: uniqueGiftSetDisplayTitlesFromDetections(detections),
    };
  }, [cartItems, country.exchangeRate, giftSets]);

  return (
    <VStack>
      {uniqueDetectedTitles.length > 0 ? (
        <VStack className="mb-3 rounded-sm border border-primary/30 bg-primary/5 px-3 py-2">
          <Text className="text-xs font-medium uppercase tracking-wide text-primary">
            {giftSetsT("gift_set_detected")}
          </Text>
          {uniqueDetectedTitles.map((title) => (
            <Text
              key={title}
              className="text-sm text-foreground"
            >
              {giftSetsT("gift_set_in_cart", { setName: title })}
            </Text>
          ))}
        </VStack>
      ) : null}
      <VStack>
        {cartItems.map((item) => (
          <CartItem
            key={`${item.product.id}-${item.variant.id}`}
            item={item}
          />
        ))}
      </VStack>
      {/* Order summary */}
      <VStack className="space-y-4 py-4">
        {totalSavings > 0 ? (
          <>
            <HStack className="justify-between">
              <Text className="text-muted-foreground">{shoppingCartT("subtotal_items")}</Text>
              <Text className="font-price font-medium text-muted-foreground">
                {country.symbol}
                {Number(lineSubtotal).toFixed(2)}
              </Text>
            </HStack>
            <HStack className="justify-between">
              <Text className="font-medium text-primary">
                {shoppingCartT("gift_set_bundle_savings")}
              </Text>
              <Text className="font-price font-medium text-primary">
                −{country.symbol}
                {Number(totalSavings).toFixed(2)}
              </Text>
            </HStack>
          </>
        ) : (
          <HStack className="justify-between">
            <Text className="font-medium">{shoppingCartT("subtotal")}</Text>
            <Text className="font-price font-medium">
              {country.symbol}
              {Number(lineSubtotal).toFixed(2)}
            </Text>
          </HStack>
        )}
        <Separator />
        <HStack className="justify-between">
          <Text className="font-medium">{deliveryLineLabel}</Text>
          <Text className="font-price font-medium">
            {country.symbol}
            {Number(shippingFee).toFixed(2)}
          </Text>
        </HStack>
        <HStack className="justify-between">
          <Text className="font-medium">{shoppingCartT("total")}</Text>
          <Text className="font-price font-medium">
            {country.symbol}
            {Number(adjustedSubtotal + shippingFee).toFixed(2)}
          </Text>
        </HStack>
      </VStack>
    </VStack>
  );
};
